import React, { Suspense } from 'react';
import { FaRegFileAlt, FaWrench, FaBell, FaFileContract, FaBook, FaGraduationCap, FaClipboardCheck, FaClipboardList, FaQuestionCircle } from 'react-icons/fa';
import styles from './QualityManagementPage.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import './QualityManagementDarkMode.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Lazy load modals — code is only downloaded when the modal is first opened
const SOPModal = React.lazy(() => import('./SOPModal'));
const IndicationEducationModal = React.lazy(() => import('./IndicationEducationModal'));
const FormsModal = React.lazy(() => import('./FormsModal'));
const CapaModal = React.lazy(() => import('./CapaModal'));
const NotificationModal = React.lazy(() => import('./NotificationModal'));
const PolicyModal = React.lazy(() => import('./PolicyModal'));
const JobDescriptionModal = React.lazy(() => import('./JobDescriptionModal'));
const AssignmentViewModal = React.lazy(() => import('./AssignmentViewModal'));
const QuestionPoolModal = React.lazy(() => import('./QuestionPool/QuestionPoolModal'));

const QualityManagementPage = () => {
  const { isDarkMode } = useTheme();
  const [isSOPModalOpen, setIsSOPModalOpen] = React.useState(false);
  const [isIndicationEducationModalOpen, setIsIndicationEducationModalOpen] = React.useState(false);
  const [isFormsModalOpen, setIsFormsModalOpen] = React.useState(false);
  const [isCapaModalOpen, setIsCapaModalOpen] = React.useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = React.useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = React.useState(false);
  const [isJobDescriptionModalOpen, setIsJobDescriptionModalOpen] = React.useState(false);
  const [isAssignmentViewModalOpen, setIsAssignmentViewModalOpen] = React.useState(false);
  const [isQuestionPoolModalOpen, setIsQuestionPoolModalOpen] = React.useState(false);
  
  const cards = [
    {
      title: 'Capa',
      icon: <FaRegFileAlt />,
      color: '#00bcd4',
      description: 'Corrective and Preventive Action Management'
    },
    {
      title: 'Notification',
      icon: <FaBell />,
      color: '#4caf50',
      description: 'Alert Management System'
    },
    {
      title: 'SOP',
      icon: <FaFileContract />,
      color: '#9c27b0',
      description: 'Standard Operating Procedures'
    },
    {
      title: 'Policy',
      icon: <FaBook />,
      color: '#f44336',
      description: 'Company Policies and Guidelines'
    },
    {
      title: 'Job Description',
      icon: <FaWrench />,
      color: '#e91e63',
      description: 'Role and Responsibilities Definitions'
    },
    {
      title: 'Forms',
      icon: <FaRegFileAlt />,
      color: '#9e9e9e',
      description: 'Standardized Document Templates'
    },
    {
      title: 'Endikasyon Eğitim Modülü',
      icon: <FaGraduationCap />,
      color: '#ff9800',
      description: 'Indication Training Module'
    },
    {
      title: 'Atama Görüntüleme',
      icon: <FaClipboardList />,
      color: '#2196f3',
      description: 'View SOP and Training Assignments by Department'
    },
    {
      title: 'Soru Havuzu',
      icon: <FaQuestionCircle />,
      color: '#7c3aed',
      description: 'Departman bazlı SOP ve eğitim okuma sonu sınav soruları'
    },
  ];

  const handleCardClick = (title) => {
    if (title === 'SOP') {
      setIsSOPModalOpen(true);
    } else if (title === 'Endikasyon Eğitim Modülü') {
      setIsIndicationEducationModalOpen(true);
    } else if (title === 'Forms') {
      setIsFormsModalOpen(true);
    } else if (title === 'Capa') {
      setIsCapaModalOpen(true);
    } else if (title === 'Notification') {
      setIsNotificationModalOpen(true);
    } else if (title === 'Policy') {
      setIsPolicyModalOpen(true);
    } else if (title === 'Job Description') {
      setIsJobDescriptionModalOpen(true);
    } else if (title === 'Atama Görüntüleme') {
      setIsAssignmentViewModalOpen(true);
    } else if (title === 'Soru Havuzu') {
      setIsQuestionPoolModalOpen(true);
    } else {
      // For other cards that don't have modals yet
      toast.info(`${title} functionality coming soon.`);
    }
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={styles.container}>
        {/* Header Section */}
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaClipboardCheck className={styles.headerIcon} />
              Kalite Yönetimi
            </h1>
          </div>
        </header>

        {/* Stats Summary */}
        {/* <div className={styles.statsSummary}>
          <p className={styles.summaryText}>
            Kalite yönetimi, organizasyonun kalite politikasını, hedeflerini ve sorumluluklarını belirleyen 
            ve bunları kalite planlama, kalite kontrol, kalite güvence ve kalite iyileştirme gibi 
            yöntemlerle uygulayan koordineli faaliyetlerdir.
          </p>
        </div> */}

        {/* Cards Grid */}
        <div className={styles.cardsContainer}>
          {cards.map((card, index) => (
            <div 
              key={index} 
              className={styles.card}
              onClick={() => handleCardClick(card.title)}
              data-color={card.color}
            >
              <div className={styles.cardColor} style={{ backgroundColor: card.color }}></div>
              <div className={styles.cardContent}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIconWrapper} style={{ backgroundColor: `${card.color}20` }}>
                    <div className={styles.cardIcon} style={{ color: card.color }}>
                      {card.icon}
                    </div>
                  </div>
                  <div className={styles.cardInfo}>
                    <h2 className={styles.cardTitle}>{card.title}</h2>
                    <p className={styles.cardDescription}>{card.description}</p>
                  </div>
                </div>
                <div className={styles.cardFooter}>
                  <div className={styles.cardArrow}>&#8594;</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modals — only mounted when opened, code-split via React.lazy */}
        <Suspense fallback={null}>
          {isSOPModalOpen && (
            <SOPModal isOpen onClose={() => setIsSOPModalOpen(false)} />
          )}
          {isIndicationEducationModalOpen && (
            <IndicationEducationModal isOpen onClose={() => setIsIndicationEducationModalOpen(false)} />
          )}
          {isFormsModalOpen && (
            <FormsModal isOpen onClose={() => setIsFormsModalOpen(false)} />
          )}
          {isCapaModalOpen && (
            <CapaModal isOpen onClose={() => setIsCapaModalOpen(false)} />
          )}
          {isNotificationModalOpen && (
            <NotificationModal isOpen onClose={() => setIsNotificationModalOpen(false)} />
          )}
          {isPolicyModalOpen && (
            <PolicyModal isOpen onClose={() => setIsPolicyModalOpen(false)} />
          )}
          {isJobDescriptionModalOpen && (
            <JobDescriptionModal isOpen onClose={() => setIsJobDescriptionModalOpen(false)} />
          )}
          {isAssignmentViewModalOpen && (
            <AssignmentViewModal isOpen onClose={() => setIsAssignmentViewModalOpen(false)} />
          )}
          {isQuestionPoolModalOpen && (
            <QuestionPoolModal isOpen onClose={() => setIsQuestionPoolModalOpen(false)} />
          )}
        </Suspense>

        {/* Toast Container for notifications */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />
      </div>
    </div>
  );
};

export default QualityManagementPage;
